import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import GameConfig from './GameConfig';
import SeatRow from './SeatRow';
import { layoutFromLobbyData } from './seatLayout';

import './lobby-overlay.css';

// LobbyOverlay — the living lobby, rendered as the body of an in-game
// FloatingWindow (CC_LOBBY_OVERLAY_WINDOW, 2026-06-07).
//
// It is a VIEW: it never mutates lobby/group state. It renders the same
// seat roster the pre-game lobby shows (read-only) plus the game config
// read-only, from the SAME broadcast the lobby reads — no new wire.
//
// State source (recon 2 — zero new wire needs): the lobby state flows
// during PLAYING through the `group-update-event` window CustomEvent that
// GameGroupHandler.receiveGroupMembers dispatches on every Members/Join
// broadcast (claim / close / members all broadcast mid-game post the
// 06-06/07 wave). The same handler stashes the latest seats/config on
// itself (groupSeats / lobbyConfig / groupMembers / memberRecord /
// _ownerUserId). So:
//   - mount-time seed reads the groupHandler cache (covers a window
//     summoned AFTER the last broadcast — the common case in-game), and
//   - every subsequent `group-update-event` re-seeds from the event
//     detail (the living part — repaints on the next Members push).
// This mirrors Lobby.js's own seed+listen shape exactly.

// COPY PLACEHOLDER (CC_LOBBY_OVERLAY_WINDOW): the close affordance. The
// window can be left up while playing or dismissed. Michael owns wording.
const CLOSE_LABEL = 'Close';

// COPY PLACEHOLDER (CC_OVERLAY_OBSERVER_SECTION): the observer-section
// header (paired with the live count) and the empty-state line. The
// section lists who is watching the game live, for everyone in it; the
// empty state mirrors the read-only roster's labeled "Empty" seat
// convention (a one-line placeholder, not an absent section). Michael owns
// the wording.
const OBSERVERS_TITLE = 'Watching';
const OBSERVERS_EMPTY_LABEL = 'Nobody watching';


function ObserverSection({ observers, observerCount }) {
    // CC_OVERLAY_OBSERVER_SECTION: renders the live spectator roster the
    // backend emits on the Members rail (observers = display names,
    // observerCount = tally; CC_OBSERVER_ENTRY_V1 →
    // group_router._make_group_membership_message). Visible to EVERYONE in
    // the game. The empty state renders a one-line placeholder — matching
    // the read-only roster's labeled "Empty" seat convention rather than
    // vanishing. Each observer row carries a DEAD per-observer action slot
    // (`.observer-row-action`, empty): the reserved home for the owner's
    // per-observer Kick button. The BE observer-kick + the
    // gameApi.kickFromGroup wire already landed
    // (CC_KICK_SEATS_AND_OBSERVERS); a later CC drops the button into this
    // slot. v1 leaves it dead.
    const names = observers || [];
    const count = (typeof observerCount === 'number')
        ? observerCount : names.length;
    return (
        <div className="lobby-overlay-observers">
            <div className="lobby-overlay-observers-title">
                {`${OBSERVERS_TITLE} (${count})`}
            </div>
            {names.length === 0 ? (
                <div className="lobby-overlay-observers-empty">
                    {OBSERVERS_EMPTY_LABEL}
                </div>
            ) : (
                names.map((name, observerIndex) => (
                    <div className="observer-row" key={observerIndex}>
                        <div className="observer-row-name">{name}</div>
                        {/* Dead per-observer action slot — reserved home
                          * for the owner's Kick button
                          * (CC_KICK_SEATS_AND_OBSERVERS). Empty in v1. */}
                        <div className="observer-row-action" />
                    </div>
                ))
            )}
        </div>
    );
}


function _lobbyDataFromCache(handler) {
    // Mirror of Lobby.js's lobbyData useState initializer: build a
    // lobbyData shape from the groupHandler cache, or null if the cache
    // is cold (legacy fallback — layoutFromLobbyData(null) → all-empty).
    const cachedSeats = (handler && handler.groupSeats) || [];
    const cachedConfig = (handler && handler.lobbyConfig) || null;
    if (cachedSeats.length === 0 && !cachedConfig) return null;
    return {
        ...(cachedConfig || {}),
        ...(cachedSeats.length > 0 ? { seats: cachedSeats } : {}),
    };
}


function _seedFromHandler(app) {
    const handler = (app && app.game && app.game.groupHandler) || null;
    return {
        members: (handler && handler.groupMembers) || [],
        member: (handler && handler.memberRecord) || null,
        ownerUserId: (handler && handler._ownerUserId) || null,
        lobbyData: _lobbyDataFromCache(handler),
        observers: (handler && handler.observers) || [],
        observerCount: (handler && handler.observerCount) || 0,
    };
}


function _mergeLobbyData(prev, detail) {
    // Mirror of Lobby.js's handleGroupUpdate config merge: take seats when
    // present, and only the config fields actually carried on this push.
    const seats = detail.seats;
    const cfg = detail.lobbyConfig || {};
    const hasSeats = seats && seats.length > 0;
    const next = { ...(prev || {}) };
    if (hasSeats) next.seats = seats;
    if (typeof cfg.amenities === 'number') next.amenities = cfg.amenities;
    if (typeof cfg.beds === 'number') next.beds = cfg.beds;
    if (typeof cfg.debris === 'number') next.debris = cfg.debris;
    if (typeof cfg.ghosts === 'number') next.ghosts = cfg.ghosts;
    if (typeof cfg.deck === 'number') next.deck = cfg.deck;
    if (Array.isArray(cfg.expansions)) next.expansions = cfg.expansions;
    if (typeof cfg.status_id === 'number') next.status_id = cfg.status_id;
    if (cfg.current_game_id !== undefined) {
        next.current_game_id = cfg.current_game_id;
    }
    if (typeof cfg.public === 'boolean') next.public = cfg.public;
    return next;
}


export default function LobbyOverlay(props) {
    const { app, onClose } = props;
    const [state, setState] = useState(() => _seedFromHandler(app));

    useEffect(() => {
        const handleGroupUpdate = (event) => {
            const detail = event.detail || {};
            setState((prev) => ({
                members: detail.members || [],
                member: detail.member || null,
                ownerUserId: (detail.ownerUserId !== undefined
                    ? detail.ownerUserId : null),
                lobbyData: _mergeLobbyData(prev.lobbyData, detail),
                observers: detail.observers || [],
                observerCount: (detail.observerCount !== undefined
                    ? detail.observerCount
                    : (detail.observers ? detail.observers.length : 0)),
            }));
        };
        window.addEventListener('group-update-event', handleGroupUpdate);
        return () => window.removeEventListener(
            'group-update-event', handleGroupUpdate);
    }, []);

    const {
        members, member, ownerUserId, lobbyData, observers, observerCount,
    } = state;
    const seatLayout = layoutFromLobbyData(lobbyData);
    const localUserId = (member && member.user) ? member.user.id : null;
    const localMemberId = member ? member.id : null;

    // CC_KICK_SEATS_AND_OBSERVERS: is the LOCAL viewer the lobby owner?
    // Integer-id match on the broadcast ownerUserId (gate on ids, not
    // strings — same rule as Lobby.js / isOwnerSeat below). Re-derived
    // from state on every group-update-event, so it tracks the live
    // owner rather than freezing a stale value. The button is owner-only
    // UX; the server's owner-gate in kick_from_group is the authority
    // (one threat per layer).
    const isOwner = (localUserId !== null && ownerUserId !== null
        && ownerUserId !== undefined && localUserId === ownerUserId);

    // CC_KICK_SEATS_AND_OBSERVERS: in-game playing-seat kick. Same wire
    // as the pre-game lobby's handleKick (gameApi.kickFromGroup); the
    // kicked occupant goes patient (seat + engine state survive).
    const handleKick = (userId) => {
        if (!userId) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.kickFromGroup(userId);
    };

    // CC_OWNER_FILL_EMPTY_SEAT_AI: owner-only Fill-with-AI on a patiently-
    // vacated seat. Same wire as the peer-left modal's Replace path
    // (gameApi.replaceSeatWithAI -> group_router.replace_seat_with_ai); the
    // BE sets artificial_player on the surviving GamePlayer and broadcasts
    // the per-seat GamePlayer patch so the owner's FE-AI driver picks the
    // seat up and drives it. ai_id omitted -> backend default AI. SeatRow
    // gates the affordance on isOwner && the slot's `vacated` flag; the
    // server's owner-gate in replace_seat_with_ai is the authority.
    const handleFillAI = (seatIndex) => {
        if (seatIndex === null || seatIndex === undefined) return;
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.replaceSeatWithAI(seatIndex);
    };

    return (
        <div className="lobby-overlay">
            <div className="lobby-overlay-scroll">
                {/* Header zone (per the window's frame seam): the live
                  * observer list — who is watching the game — renders here
                  * for everyone in it. The public dial is still a future
                  * tenant of this zone. */}
                <ObserverSection
                    observers={observers}
                    observerCount={observerCount}
                />
                <div className="lobby-overlay-roster">
                    {seatLayout.map((slot, seatIndex) => {
                        let isLocalUser = false;
                        if (slot.kind === 'human' && slot.occupant) {
                            const occupantUserId = (
                                slot.occupant.user
                                && slot.occupant.user.id);
                            if (occupantUserId !== undefined
                                    && occupantUserId !== null
                                    && localUserId !== null) {
                                isLocalUser = occupantUserId === localUserId;
                            } else {
                                isLocalUser = (
                                    slot.occupant.id === localMemberId);
                            }
                        }
                        // CC_SEAT_IDENTITY_MARKS: does THIS seat's occupant
                        // own the lobby? Integer-id match on the occupant's
                        // user vs the broadcast ownerUserId (gate on ids,
                        // not strings) — same rule as Lobby.js.
                        let isOwnerSeat = false;
                        if (slot.kind === 'human' && slot.occupant) {
                            const occUserId = (
                                slot.occupant.user
                                && slot.occupant.user.id);
                            if (occUserId !== undefined && occUserId !== null
                                    && ownerUserId !== undefined
                                    && ownerUserId !== null) {
                                isOwnerSeat = occUserId === ownerUserId;
                            }
                        }
                        return (
                            <SeatRow
                                key={seatIndex}
                                seatNumber={seatIndex}
                                seatState={slot.kind}
                                occupant={slot.occupant || null}
                                aiIndex={
                                    slot.kind === 'ai' ? slot.aiIndex : null
                                }
                                isOwnerSeat={isOwnerSeat}
                                isLocalUser={isLocalUser}
                                isOwner={isOwner}
                                vacated={slot.vacated}
                                onKick={handleKick}
                                onFillAI={handleFillAI}
                                readOnly
                            />
                        );
                    })}
                </div>
                <div className="lobby-overlay-config">
                    {/* Config is read-only for EVERYONE in-game. The seam
                      * for the future in-window public dial lives behind
                      * GameConfig's readOnly gate. */}
                    <GameConfig
                        app={app}
                        lobbyData={lobbyData}
                        readOnly
                    />
                </div>
            </div>
            <div className="lobby-overlay-footer">
                {/* Footer zone — the other future home for kick / public
                  * dial / observer controls. v1 carries only Close. */}
                <Button
                    variant="dread"
                    onClick={onClose}
                    className="lobby-overlay-close"
                >
                    {CLOSE_LABEL}
                </Button>
            </div>
        </div>
    );
}
